// Verified content from the live faemse.org crawl (Aug 2026) unless marked SAMPLE.

// Flip to true only after President Anzardo signs off on the events calendar,
// news posts, association statistics, and his welcome message. While false,
// sample sections are hidden or visibly labeled as previews on the public site.
export const CONTENT_VERIFIED = false;

// Composition per bylaws 5.02: President, President-Elect, Immediate Past
// President, Secretary, and three Members-at-Large (seven voting seats). The
// EMS Educator representative to the state EMS Advisory Council sits ex
// officio without a vote (5.02.01), shown as a seat of its own since
// 2026-10-22 (it had shared the Immediate Past President's card, which read
// as if it voted). The Executive Director is appointed staff (9.05), not a
// director. Names confirmed current by the board, Sept 2026.
// Headshots and personal bios still to come from the board.
export type BoardMember = {
  role: string;
  name: string;
  blurb: string;
  kind: 'officer' | 'director' | 'staff';
  // Sits with the board without a vote (bylaws 5.02.01).
  exOfficio?: boolean;
};

const atLarge = 'One of three directors elected by the Active membership to represent it on the board.';

export const board: BoardMember[] = [
  {
    kind: 'officer',
    role: 'President',
    name: 'Jorge Anzardo',
    blurb:
      'Chief executive officer of the association. Presides at board and membership meetings, conducts its routine business with the Executive Director, and sits ex officio on every committee.',
  },
  {
    kind: 'officer',
    role: 'President-Elect',
    name: 'Bryan Spangler',
    blurb:
      'Acts for the President whenever the President is absent, and succeeds to the presidency at the end of the term.',
  },
  {
    kind: 'officer',
    role: 'Secretary',
    name: 'Rochelle Goldberg',
    blurb:
      'Custodian of the association\'s records and minutes. Reviews membership applications, sends the notices the bylaws require, and oversees elections.',
  },
  {
    kind: 'director',
    role: 'Immediate Past President',
    name: 'Melissa McNally',
    blurb:
      'Advisor and consultant to the board, and chair of the Nominating Committee.',
  },
  { kind: 'director', role: 'Member-at-Large', name: 'Matt Keeler', blurb: atLarge },
  { kind: 'director', role: 'Member-at-Large', name: 'Garth Richards', blurb: atLarge },
  { kind: 'director', role: 'Member-at-Large', name: 'Carlos Tavarez', blurb: atLarge },
  {
    kind: 'director',
    exOfficio: true,
    role: 'EMS Educator Representative to the Florida EMS Advisory Council',
    name: 'Melissa McNally',
    blurb:
      "The EMS educators' representative on the state EMS Advisory Council. Under the bylaws the representative may sit with this board ex officio: attending board meetings and taking part in discussions and committees, without making motions or voting.",
  },
  {
    kind: 'staff',
    role: 'Executive Director',
    name: 'James Dinsch',
    blurb:
      'Appointed by the board as chief operating officer. Runs day-to-day operations: finances, records, dues, and this website. Attends board meetings; not a voting director.',
  },
];

// The FAEMSE Foundation's board, as the board listed it (2026-10-22). The
// Foundation page reads the foundation_board table (Admin → Foundation page);
// this copy stands in only if the database can't be reached.
export const foundationBoard: { role: string; name: string }[] = [
  { role: 'President', name: 'Angel Nater' },
  { role: 'Executive Director', name: 'Louis Mallory' },
  { role: 'Secretary', name: 'Melissa McNally' },
  { role: 'At Large', name: 'Bill McGrath' },
  { role: 'At Large', name: 'Phil Borum' },
];

// Four classes of membership under bylaws 2.02. Dues are set by the board
// (4.01); these amounts were confirmed by the board in Sept 2026. Honorary
// carries no dues and is described separately below.
export const tiers = [
  {
    name: 'Active',
    who: 'Individuals who plan, supervise, teach, or practice out-of-hospital care',
    price: '$50',
    per: '/ year',
    featured: true,
    perks: [
      'Vote in elections and hold office',
      'Serve on and chair committees',
      'Nominate and vote for EMS Educator of the Year',
      'Q&A archive, teaching videos, and the member library',
    ],
  },
  {
    name: 'Institutional',
    who: 'Organizations that plan, supervise, teach, or practice out-of-hospital care: colleges, academies, EMS programs',
    price: '$250',
    per: '/ year',
    featured: false,
    perks: [
      'Up to five named representatives',
      'Each qualifying representative holds full Active privileges',
      'Vote, hold office, and serve on committees',
      'Archive, videos, and library for every representative',
    ],
  },
  {
    name: 'Corporate',
    who: 'Companies, associations, and government agencies with an interest in EMS',
    price: '$200',
    per: '/ year',
    featured: false,
    perks: [
      'Up to five named representatives',
      'Serve on committees (non-voting)',
      'Recognized as a corporate sponsor on this site',
      'Archive, videos, and library access',
    ],
  },
];

// Bylaws 2.02.02. Not a tier anyone applies for.
export const honorary = {
  name: 'Honorary',
  who: 'Elected by the Board of Directors for outstanding dedication to EMS and the association; members may nominate. A lifetime title with no dues. Honorary members do not vote, hold office, or chair committees.',
};

// Membership terms the site states in plain words. Sources: board decision
// (12-month term), bylaws 4.01 (dues notice) and 2.05 (90-day revocation).
export const membershipTerms = [
  'Membership runs twelve months from your last dues payment.',
  'Dues are set by the Board of Directors. Any change is announced to members 30 days before it takes effect.',
  'If dues go unpaid, you keep member access for 90 days after your term ends. After that the bylaws allow the membership to be revoked.',
  'Applications are reviewed by the Secretary under the bylaws. An applicant who is denied may appeal to the Board of Directors.',
];

export const resourceCategories = [
  {
    category: 'Accreditation & Standards',
    links: [
      { name: 'CoAEMSP', url: 'https://coaemsp.org' },
      { name: 'CAAHEP', url: 'https://www.caahep.org' },
      { name: 'National Registry of EMTs (NREMT)', url: 'https://www.nremt.org' },
    ],
  },
  {
    category: 'State & Federal',
    links: [
      { name: 'Florida DOH — EMS Section', url: 'https://www.floridahealth.gov/licensing-and-regulation/ems-system/index.html' },
      // The Bureau's program page: approved-program map, program applications,
      // and the NREMT pass/fail rates by school (board request, Sept 2026).
      {
        name: 'Florida DOH — EMS training programs & NREMT pass rates by school',
        url: 'https://www.floridahealth.gov/licensing-regulations/regulated-professions/emergency-medical-technicians-and-paramedics/ems-training-and-education-programs/',
      },
      { name: 'NHTSA Office of EMS', url: 'https://www.ems.gov' },
      { name: 'FICEMS', url: 'https://www.ems.gov/ficems.html' },
    ],
  },
  {
    category: 'Professional Organizations',
    links: [
      { name: 'NAEMSE', url: 'https://naemse.org' },
      { name: 'NAEMSP', url: 'https://naemsp.org' },
      { name: 'ACEP', url: 'https://www.acep.org' },
      { name: 'FAEMSE Foundation', url: 'https://www.faemsefoundation.org' },
    ],
  },
  {
    category: 'Curriculum & Education',
    links: [
      // Deep links to ems.gov/education.html and naemse.org/resources broke after
      // both sites reorganized (Aug 2026); pointing at the working top-level pages
      // until the association confirms the new permanent URLs.
      { name: 'National EMS Education Standards (EMS.gov)', url: 'https://www.ems.gov' },
      { name: 'CAPCE (formerly CECBEMS)', url: 'https://capce.org' },
      { name: 'NAEMSE Educator Resources', url: 'https://naemse.org' },
    ],
  },
];

// Corporate sponsors are the association's Corporate members (bylaws
// 2.02.03). List confirmed by the board, Sept 2026. `logo` names a file in
// public/sponsors/ (webp), or null until the company supplies one; the site
// then shows the name in its place. `url` is the sponsor's website (found by
// web search, Sept 2026; the board is checking them), or '' when none is on
// file, in which case the name is shown without a link.
export const sponsors: { name: string; logo: string | null; url: string }[] = [
  { name: '3B Scientific', logo: null, url: 'https://www.3bscientific.com/' },
  { name: 'American Medical Academy', logo: 'ama', url: 'https://ama.edu/' },
  { name: 'Braxton College', logo: null, url: 'https://braxton.edu/' },
  { name: 'Coral Springs Regional Institute of Public Safety', logo: 'csrips', url: 'https://www.csrips.org/' },
  { name: 'Dinsch Consulting Group', logo: null, url: '' },
  { name: 'Emergency Education Institute', logo: 'eei', url: 'https://eei.edu/' },
  { name: 'Emergency Educational Training Institute, Inc.', logo: 'eeti', url: 'https://eeti.org/' },
  { name: 'EMETSEEI Institute, Inc.', logo: 'emetseei', url: 'https://emetseei.edu/' },
  { name: 'First Response Training Group', logo: null, url: 'https://www.frtg.edu/' },
  { name: 'Henry Schein', logo: 'henry-schein', url: 'https://www.henryschein.com/' },
  { name: 'Limmer Education', logo: null, url: 'https://limmereducation.com/' },
  { name: 'Medical Career Academy', logo: 'mca', url: 'https://mcaedu.org/' },
  { name: 'Platinum Educational Group', logo: 'platinum-ed', url: 'https://www.platinumed.com/' },
  { name: 'Public Safety Group', logo: 'jblpsg', url: 'https://www.psglearning.com/' },
  { name: 'Southeastern Medical Academy', logo: 'sema', url: 'https://www.southeasternmedicalacademy.edu/' },
  { name: 'The Rescue Company 1', logo: 'the-rescco', url: 'https://www.therescuecompany1.com/' },
];

// ---------------------------------------------------------------------------
// Sample listings carry dates relative to today, so placeholders never look
// stale — a sample calendar full of "recently held" events is exactly the
// abandoned look the rebuild exists to avoid.
export const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function shift(days: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d;
}
const longDate = (d: Date) => d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
const monthYear = (d: Date) => d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
// No sample events (board, 2026-10-20): a made-up meeting can be taken for a
// real one. The calendar falls back to the visitor's last saved copy of the
// real one instead (lib/content.ts).

export function eventDate(e: { day: string; month: string; year: string }): Date {
  return new Date(Number(e.year), MONTHS.indexOf(e.month.toUpperCase()), Number(e.day));
}

// SAMPLE posts — replace with real association news.
export const news = [
  {
    date: longDate(shift(-12)),
    tag: 'Awards',
    title: 'EMS Educator of the Year nominations open',
    excerpt:
      'One standard: educators whose students are measurably better for having been in their classroom.',
    body: '',
  },
  {
    date: longDate(shift(-27)),
    tag: 'Resources',
    title: 'New toolkit: aligning your program to the current Education Standards',
    excerpt:
      'A members-only crosswalk for mapping curriculum to the latest National EMS Education Standards.',
    body: '',
  },
  {
    date: longDate(shift(-46)),
    tag: 'Board',
    title: 'New directors join the FAEMSE board',
    excerpt:
      'Meet the educators stepping into leadership this term — and the priorities guiding the association.',
    body: '',
  },
];

// No sample jobs or classes either (board, 2026-10-20): an outage shows the
// visitor's saved copy of the real boards instead (lib/postings.ts).

// SAMPLE Q&A entries — the archive launches with distilled listserv threads.
export const sampleQa = [
  {
    date: monthYear(shift(-10)),
    topic: 'Program Director',
    question: 'A new program director just took over — what deadlines are already running?',
    answer:
      'Two clocks start immediately: CoAEMSP must be notified of the personnel change within 30 calendar days, and the new director must complete the required workshop within 15 months of assuming the role. Verify both against current CoAEMSP standards — the details change. The program director starter guide on this site walks through the first 90 days.',
  },
  {
    date: monthYear(shift(-40)),
    topic: 'Clinical',
    question: 'How are other programs handling clinical site competition in metro areas?',
    answer:
      'Consensus from the thread: diversify beyond the big hospital systems (free-standing EDs, interfacility transport services), formalize preceptor recognition so sites see value, and coordinate rotation calendars with neighboring programs instead of competing for the same weeks.',
  },
  {
    date: monthYear(shift(-70)),
    topic: 'Teaching',
    question: 'What actually moves NREMT pass rates for a struggling cohort?',
    answer:
      'The recurring answers: item-writing practice for faculty (most program exams under-prepare students for NREMT-style questions), early identification using unit exam data rather than waiting for the final, and structured remediation with a contract — not open-ended “study more.”',
  },
  {
    date: monthYear(shift(-100)),
    topic: 'State & Policy',
    question: 'Where do Florida rule changes actually get announced?',
    answer:
      'The state EMS office publishes through the Florida DoH EMS section page and the advisory council meeting cycle — there is no RSS feed, so FAEMSE summarizes anything affecting educators in the news feed on this site, with a line of plain-English context.',
  },
];

// Teaching videos: the president has parked this until instructors are lined
// up, so there are no sample titles — the page shows its in-development state.
export const sampleVideos: { topic: string; title: string; presenter: string; minutes: number }[] = [];

// The welcome slide, as the board last saved it. It is a row in the
// spotlights table like the rest (is_welcome), edited in the Spotlights panel;
// this copy only stands in while the rows load on a first visit, or if the
// table can't be reached, so keep it in step with the live row: it sets the
// hero's height, and a different wording here would make the hero jump when
// the real row arrives.
// The three small tags under a slide's buttons. Every slide has three,
// each with its own show switch; these are the words a new slide starts
// with (and the database's column defaults).
export const defaultSlideTags = ['501(c)(6) nonprofit', 'Every program type, statewide', 'Statewide meetings & workshops'];

export const welcomeSlide = {
  kicker: 'Florida Association of EMS Educators',
  title: "Supporting the educators\nwho train Florida's\nfirst responders",
  body: 'FAEMSE is the statewide professional home for EMS instructors, program directors, and training officers — the network, the resources, and the policy voice behind better EMT and paramedic education.',
  imageUrl: '',
  videoUrl: '',
  linkUrl: '/events',
  linkLabel: "See what's coming up",
  tags: defaultSlideTags,
  welcome: true,
};

export const faq = [
  {
    q: 'Who can become a member?',
    a: 'Anyone involved or interested in the education and training of EMS and out-of-hospital personnel: instructors, program directors, training officers, preceptors, agency educators, and the institutions and companies that support them. Applicants are considered without regard to race, age, gender, creed, or color.',
  },
  {
    q: 'What does membership cost?',
    a: 'Active (individual) membership is $50 a year. Institutional membership is $250 a year and covers up to five representatives with Active privileges. Corporate membership is $200 a year with up to five representatives. Honorary membership is by board election and carries no dues. Dues are set by the board, and any change is announced to members 30 days before it takes effect.',
  },
  {
    q: 'How long does membership last?',
    a: 'Twelve months from your last dues payment. If dues go unpaid, you keep member access for 90 days after your term ends; after that the bylaws allow the membership to be revoked.',
  },
  {
    q: 'How are applications handled?',
    a: 'The Secretary or a designee reviews each application under the bylaws. Approved members are invoiced for dues and set up with a portal account. An applicant who is denied may appeal to the Board of Directors.',
  },
  {
    q: 'When and where does the association meet?',
    a: 'Regular membership meetings are held through the year, usually alongside Florida\'s statewide EMS conferences, and the annual meeting is held mid-year. Dates and locations post to the calendar as the board confirms them.',
  },
  {
    q: 'How do I get involved beyond attending?',
    a: 'Active members in good standing may run for the board. Members-at-Large are elected by ballot in odd-numbered years; the President-Elect and Secretary in even-numbered years, and the President-Elect succeeds to the presidency. You can also serve on a committee, record a short teaching video, contribute to the program director guide, judge a student competition, or present at a workshop.',
  },
];

// The public outline of the bylaws, one entry per article of the current
// revision (September 10, 2021). The full text is members-only, in the portal.
export const bylawsOutline = [
  {
    article: 'Article 1',
    title: 'Introduction and name',
    text: 'The Florida Association of Emergency Medical Services Educators, a Florida not-for-profit 501(c)(6) corporation. Mission: to provide resources to individuals and organizations that will foster excellence in EMS education and training. Vision: to be the foremost resource within Florida\'s EMS educational community.',
  },
  {
    article: 'Article 2',
    title: 'Membership',
    text: 'Open to anyone involved or interested in EMS and out-of-hospital education, without regard to race, age, gender, creed, or color. Four classes: Active, Honorary, Corporate, and Institutional. Applications are reviewed by the Secretary; a denial may be appealed to the board. Membership may be revoked once dues are 90 days past due.',
  },
  {
    article: 'Article 3',
    title: 'Meetings',
    text: 'Regular meetings are usually held in conjunction with the Florida EMS Advisory Council and constituency group meetings. The annual meeting is held mid-year.',
  },
  {
    article: 'Article 4',
    title: 'Dues',
    text: 'Set by the Board of Directors from the annual operating budget. Any change is communicated to the membership 30 days before it takes effect.',
  },
  {
    article: 'Article 5',
    title: 'Board of Directors',
    text: 'Seven voting seats: President, President-Elect, Immediate Past President, Secretary, and three Members-at-Large. The EMS Educator representative to the state EMS Advisory Council sits ex officio without a vote. Directors must be Active members in good standing, serve two-year terms, and are elected by ballot in odd-numbered years. Half the board is a quorum; meetings follow Robert\'s Rules of Order.',
  },
  {
    article: 'Article 6',
    title: 'Officers',
    text: 'President, President-Elect, and Secretary, elected by the membership in even-numbered years for two-year terms; the President-Elect succeeds to the presidency. The Secretary may serve two consecutive terms. No director or committee member is paid for serving.',
  },
  {
    article: 'Article 7',
    title: 'Committees',
    text: 'Three standing committees, Primary Education, Continuing Education, and Preceptor / Training Officer, plus ad hoc committees. Chairs are Active members in good standing appointed by the President for two-year terms, and together form the President\'s Council.',
  },
  {
    article: 'Article 8',
    title: 'Liaisons and representatives',
    text: 'The President appoints liaisons to organizations with similar goals. Outside organizations may send non-voting representatives to board meetings.',
  },
  {
    article: 'Article 9',
    title: 'Operations',
    text: 'The Executive Director, appointed by the board as chief operating officer, runs day-to-day operations: finances, records, dues, and the website. The fiscal year is January 1 to December 31.',
  },
  {
    article: 'Article 10',
    title: 'Amendments',
    text: 'Proposed amendments are submitted in writing and posted for review for 30 days before a vote. Adoption requires a two-thirds majority of the Active members voting.',
  },
  {
    article: 'Article 11',
    title: 'Prohibition of dividends',
    text: 'No part of the association\'s net earnings may benefit any member, officer, or private person.',
  },
  {
    article: 'Article 12',
    title: 'Finances',
    text: 'The board sets a budget for each fiscal year and operates under generally accepted accounting principles.',
  },
  {
    article: 'Article 13',
    title: 'Notice and waiver of notice',
    text: 'Notice may be given by mail, telephone, email, or other written or electronic means, and may be waived in writing.',
  },
  {
    article: 'Article 14',
    title: 'Indemnification and liability',
    text: 'The association indemnifies its directors, officers, and agents to the fullest extent of Florida law. Members are not liable for the association\'s debts.',
  },
  {
    article: 'Article 15',
    title: 'Termination',
    text: 'The association may be dissolved by a three-fourths vote of the board. Remaining assets go to organizations exempt under Section 501(c)(3).',
  },
];

export const bylawsHistory = [
  { date: 'November 29, 1997', event: 'Initially prepared' },
  { date: 'May 23, 1998', event: 'Adopted' },
  { date: 'January 23, 2008', event: 'Revised' },
  { date: 'June 29, 2009', event: 'Revised' },
  { date: 'April 2010', event: 'Revised' },
  { date: 'January 23, 2019', event: 'Revised' },
  { date: 'September 10, 2021', event: 'Revised. Current edition.' },
];

// DRAFT welcome message — for President Anzardo to approve or rewrite in his own words.
export const presidentMessage = {
  name: 'Jorge Anzardo',
  role: 'President, FAEMSE',
  quote:
    'Every EMT and paramedic in Florida can be traced back to an educator who refused to accept ‘good enough.’ This association exists so that none of those educators ever has to raise the standard alone.',
};

// The Educator of the Year program. Active members nominate and vote; the
// nominee need not be a member (board, Sept 2026). The number of categories
// is deliberately not stated until the board confirms it.
export const honors = {
  title: 'EMS Educator of the Year',
  blurb:
    'One standard: educators whose students are measurably better for having been in their classroom. Nominated and chosen by Active members, honored by the association, announced each year.',
};

export const contact = {
  legalName: 'Florida Association of Emergency Medical Services Educators, Inc.',
  taxStatus: '501(c)(6) not-for-profit corporation',
  address: '7901 4th Street #9219, St. Petersburg, FL 33702',
  // No public email address (board, 2026-10-21): info@faemse.org has no
  // mailbox, so every "write to us" on the site goes to the contact form.
  // Messages land in Admin → Inbox and notify-board emails them to the board.
  facebook: 'https://www.facebook.com/flemseducators',
  linkedin: 'https://www.linkedin.com/company/florida-association-of-ems-educators/',
};

// Link for the site's "send us X" buttons: the contact form, with the
// subject filled in when one is given.
export function contactPath(subject = ''): string {
  return subject ? `/contact?subject=${encodeURIComponent(subject)}` : '/contact';
}
